import { SignJWT, jwtVerify } from 'jose'

import { deleteCookie, getCookie, setCookie } from './cookie'
import { getTokenInfo } from '../auth'

export type Session = {
  uid?: number
  accessToken: string
  refreshToken: string
  firebaseToken: string
  accessTokenExpiresAt: number
  isHost: boolean
  name: string
  aid: string
  rememberMe: boolean
  email: string
  blocked?: boolean
  certified?: boolean
  expiresAt?: number
  profileImageUrl?: string
  verificationStatus?:
    | 'NONE' // 인증 정보 없음
    | 'HOLD' // 인증 보류
    | 'CERTIFIED' // 휴대폰 본인 인증 완료
    | 'PENDING' // 승인 대기
    | 'EMAIL_SENT' // 인증 이메일 발송됨
    | 'ID_VERIFIED' // 신분증 인증 완료
}

export const MAX_AGE = 60 * 60 * 24 * 30 // 세션 만료 시간(sec) : 30일

export const isSecureCookieRequired = process.env.NODE_ENV === 'production'

export const sessionCookieName = isSecureCookieRequired
  ? '__Secure-session-token'
  : 'session-token'

const secretKey = process.env.NEXTAUTH_SECRET
const encodedKey = new TextEncoder().encode(secretKey)

export async function encrypt(payload: Session) {
  const { rememberMe } = payload

  const jwt = new SignJWT(payload)
    .setProtectedHeader({ alg: 'HS256' })
    .setIssuedAt()

  // rememberMe가 true일 때만 만료 시간 설정
  if (rememberMe) {
    jwt.setExpirationTime(Math.floor(Date.now() / 1000) + MAX_AGE)
  }

  return jwt.sign(encodedKey)
}

export async function decrypt(session: string | undefined = '') {
  try {
    const { payload } = await jwtVerify(session, encodedKey, {
      algorithms: ['HS256'],
    })

    return payload
  } catch {
    return null
  }
}

export const getSession = async () => {
  const session = await getCookie(sessionCookieName)

  if (!session) {
    return null
  }

  const payload = await decrypt(session)

  return payload as Session
}

export async function createSession(payload: Session) {
  const { expiresAt } = payload
  const session = await encrypt(payload)

  await setCookie(sessionCookieName, session, {
    httpOnly: true,
    secure: isSecureCookieRequired,
    sameSite: 'lax',
    path: '/',
    expires: expiresAt ? new Date(expiresAt) : undefined,
  })
}

export async function updateSession(newSession: Partial<Session>) {
  const session = await getCookie(sessionCookieName)

  if (!session) {
    throw new Error('session not found')
  }

  const payload = await decrypt(session)

  if (!payload) {
    throw new Error('decrypt session failed')
  }

  const encryptedSession = await encrypt({
    ...payload,
    ...newSession,
  } as Session)

  const { expiresAt } = payload as Session

  await setCookie(sessionCookieName, encryptedSession, {
    httpOnly: true,
    secure: isSecureCookieRequired,
    sameSite: 'lax',
    path: '/',
    expires: expiresAt ? new Date(expiresAt) : undefined,
  })
}

export async function deleteSession() {
  await deleteCookie(sessionCookieName, {
    secure: isSecureCookieRequired,
    path: '/',
  })
}

export async function updateSessionFromTokens({
  accessToken,
  refreshToken,
}: Pick<Session, 'accessToken' | 'refreshToken'>) {
  const { accessTokenExpiresAt, isHost, name, aid } = getTokenInfo(accessToken)

  const newSession = {
    accessToken,
    refreshToken,
    accessTokenExpiresAt,
    isHost,
    name,
    aid,
  }

  await updateSession(newSession)
}
